import { cn } from "@/lib/utils";
import { cjk } from "@streamdown/cjk";
import { Check, Copy } from "lucide-react";
import { Streamdown } from "streamdown";

const streamdownPlugins = { cjk };

interface ChatMessageProps {
  role: string;
  content: string;
  messageIndex: number;
  isCopied: boolean;
  onCopy: (content: string, index: number) => void;
}

export function ChatMessage({ role, content, messageIndex, isCopied, onCopy }: ChatMessageProps) {
  const isUser = role === "user";

  return (
    <article className={cn("group flex w-full", isUser ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "relative min-w-0",
          isUser
            ? "max-w-[86%] rounded-lg border border-white/10 bg-white/[0.065] px-4 py-3 text-white sm:max-w-[78%]"
            : "w-full py-1 text-[#f9f9f9]"
        )}
      >
        <div
          className={cn(
            "mb-2 text-[11px] font-semibold uppercase",
            isUser ? "text-right text-white/40" : "text-orange-300"
          )}
        >
          {isUser ? "You" : "Chicky"}
        </div>
        <div
          className={cn(
            "prose prose-invert max-w-none text-[15px] leading-7",
            isUser ? "text-white/90" : "text-white/85"
          )}
        >
          <Streamdown
            plugins={streamdownPlugins}
            components={{
              p: ({ children }) => <p className="mb-3 last:mb-0 text-current">{children}</p>,
              ul: ({ children }) => (
                <ul className="my-3 space-y-2 list-disc list-inside text-current">{children}</ul>
              ),
              ol: ({ children }) => (
                <ol className="my-3 space-y-2 list-decimal list-inside text-current">{children}</ol>
              ),
              li: ({ children }) => <li className="text-current">{children}</li>,
              strong: ({ children }) => (
                <strong className={isUser ? "font-bold text-white" : "font-bold text-orange-200"}>
                  {children}
                </strong>
              ),
              em: ({ children }) => (
                <em className={isUser ? "italic text-white/80" : "italic text-orange-100/80"}>
                  {children}
                </em>
              ),
              code: ({ children }) => (
                <code className="rounded bg-black/25 px-1.5 py-0.5 font-mono text-xs text-orange-200">
                  {children}
                </code>
              ),
              pre: ({ children }) => (
                <pre className="my-3 overflow-x-auto rounded-md border border-white/10 bg-black/25 p-3 font-mono text-sm text-orange-200">
                  {children}
                </pre>
              ),
              blockquote: ({ children }) => (
                <blockquote className="my-3 border-l-2 border-orange-300/45 pl-4 italic text-white/70">
                  {children}
                </blockquote>
              ),
              h1: ({ children }) => (
                <h1 className="mb-3 mt-4 text-xl font-bold text-white first:mt-0">{children}</h1>
              ),
              h2: ({ children }) => (
                <h2 className="mb-2 mt-3 text-lg font-bold text-white first:mt-0">{children}</h2>
              ),
              h3: ({ children }) => (
                <h3 className="mb-2 mt-3 text-base font-bold text-white first:mt-0">{children}</h3>
              ),
              hr: () => <hr className="my-4 border-white/10" />,
              a: ({ children, href }) => (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-orange-300 underline underline-offset-2 transition-colors hover:text-orange-200"
                >
                  {children}
                </a>
              ),
            }}
          >
            {content}
          </Streamdown>
        </div>

        <button
          type="button"
          onClick={() => onCopy(content, messageIndex)}
          className={cn(
            "mt-2 flex h-8 items-center gap-1.5 rounded-md px-2 text-xs text-white/35 transition-colors hover:bg-white/[0.05] hover:text-white/70",
            isUser ? "ml-auto" : "-ml-2"
          )}
          aria-label={isCopied ? "Message copied" : "Copy message"}
        >
          {isCopied ? <Check size={13} className="text-green-400" /> : <Copy size={13} />}
          <span>{isCopied ? "Copied" : "Copy"}</span>
        </button>
      </div>
    </article>
  );
}
